import React, { useState, useMemo, useEffect, useContext } from 'react';
import { Modal, Button, Checkbox, Select, Input, Spin, Alert, message } from 'antd';
import { CreditCardOutlined, DownOutlined } from '@ant-design/icons';
import CompanyContext from '../../../../contexts/company/CompanyContext';
import AuthenticationContext from '../../../../contexts/auth/AuthenticationContext';
import PlansContext from '../../../../contexts/plans/PlansContext';
import { getSubscriptionInfo, SubscriptionInfo } from '../../../../../domain/use_cases/stripe/getSubscriptionInfo';
import { createBillingPortalSession } from '../../../../../domain/use_cases/stripe/createBillingPortalSession';
import { formatPlanLeedbucks } from '../../../../helpers';
import { billingIntervalAsSuffix, EBillingInterval } from '@leedpulse-monorepo/enums';
import { useLeedbucksConfig } from '../../../../hooks/plans/useLeedbucksConfig';
import dayjs from 'dayjs';
import './OrderNumberModal.css';

interface OrderNumberModalProps {
  isOpen: boolean;
  isLoading?: boolean;
  onCancel: () => void;
  onConfirm: (order: OrderData) => void;
}

export interface OrderData {
  quantity: number;
  paymentType: 'annual' | 'monthly';
  plan: string;
  advancedMode: boolean;
  /** One entry per number ordered — { plan_id } — numbers auto-assigned by backend */
  items: Array<{ plan_id: string }>;
  totalMonthly: number;
}

const QUANTITY_OPTIONS = [1, 2, 3, 5, 10];
const MAX_QUANTITY = 50;

const OrderNumberModal: React.FC<OrderNumberModalProps> = ({
  isOpen,
  isLoading = false,
  onCancel,
  onConfirm,
}) => {
  const { activeCompany, hasValidPayment, hasValidPaymentLoading } = useContext(CompanyContext);
  const { authentication } = useContext(AuthenticationContext);
  const { planCache, plans: allPlans, plansLoading, fetchPlans } = useContext(PlansContext);
  const { callRate, smsRate } = useLeedbucksConfig();

  const [quantity, setQuantity] = useState<number>(1);
  const [customQuantity, setCustomQuantity] = useState<string>('');
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [advancedMode, setAdvancedMode] = useState<boolean>(false);
  const [numberPlans, setNumberPlans] = useState<{ [key: number]: string }>({}); // slotIndex → plan ID
  const [billingPortalLoading, setBillingPortalLoading] = useState(false);

  // Stripe subscription info (for proration display)
  const [subInfo, setSubInfo] = useState<SubscriptionInfo | null>(null);
  const [subInfoLoading, setSubInfoLoading] = useState(false);

  // A point of sale bills on one rhythm. Stripe first: the mirror is half built between the
  // signature and the first webhook, and absent until the billing sync has run. Null on a
  // company with no subscription yet — its first order decides, and both rhythms are offered.
  const wantedInterval = subInfo?.interval ?? activeCompany?.billing?.interval ?? null;
  const paymentType: 'annual' | 'monthly' = wantedInterval === EBillingInterval.YEAR ? 'annual' : 'monthly';

  // Before the onboarding the cycle still has to be re-anchored, so those orders go through the
  // back-office. The date is half the condition, as on the backend: the flag alone is set at
  // every signature and would shut the portal to all of them.
  const onboardingDate = activeCompany?.billing?.onboarding_date ? dayjs(activeCompany.billing.onboarding_date) : null;
  const isAwaitingOnboarding = activeCompany?.billing?.onboarding_pending === true && !!onboardingDate;

  const effectiveQuantity = customQuantity ? parseInt(customQuantity, 10) || 0 : quantity;

  // ── Fetch plans when modal opens ──
  useEffect(() => {
    if (!isOpen) return;
    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeCompany]);

  // Only the plans on the company's rhythm: an annual plan must never show up in a monthly
  // list, priced as if it were monthly.
  const plans = useMemo(
    () => (wantedInterval === null
      ? allPlans
      : allPlans.filter((plan) => (plan.price?.interval ?? EBillingInterval.MONTH) === wantedInterval)),
    [allPlans, wantedInterval],
  );

  // ── Keep selected plan id in sync with available plans ──
  useEffect(() => {
    if (plans.length === 0) {
      if (selectedPlanId !== null) setSelectedPlanId(null);
      return;
    }

    // If nothing selected yet, or selected id is no longer available, pick a default.
    const hasSelected = selectedPlanId ? planCache.has(selectedPlanId) : false;
    if (!selectedPlanId || !hasSelected) {
      setSelectedPlanId(plans[0].id);
    }
  }, [plans, planCache, selectedPlanId]);

  // ── Fetch Stripe subscription info when modal opens ──
  useEffect(() => {
    if (!isOpen || !activeCompany || !authentication) {
      setSubInfo(null);
      return;
    }

    let cancelled = false;
    setSubInfoLoading(true);

    getSubscriptionInfo(activeCompany.id, activeCompany.api_key, authentication)
      .then((info) => {
        if (!cancelled) setSubInfo(info);
      })
      .catch((err) => {
        console.error('Failed to fetch subscription info:', err);
        if (!cancelled) setSubInfo(null);
      })
      .finally(() => {
        if (!cancelled) setSubInfoLoading(false);
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeCompany]);

  // ── Handlers ──

  const handleQuantityClick = (q: number) => {
    setQuantity(q);
    setCustomQuantity('');
  };

  const handleCustomQuantityChange = (value: string) => {
    const numValue = value.replace(/\D/g, '');
    const numericValue = parseInt(numValue, 10);
    if (numericValue > MAX_QUANTITY) {
      setCustomQuantity(MAX_QUANTITY.toString());
    } else {
      setCustomQuantity(numValue);
    }
  };

  const handleNumberPlanChange = (slotIndex: number, planId: string) => {
    setNumberPlans((prev) => ({ ...prev, [slotIndex]: planId }));
  };

  const getPlanForSlot = (slotIndex: number): string => {
    return numberPlans[slotIndex] || selectedPlanId || '';
  };

  const formatPrice = (price: number): string => {
    return price.toLocaleString('fr-FR', {
      minimumFractionDigits: price % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    });
  };

  // ── Build the items array for the backend ──
  const orderItems = useMemo((): Array<{ plan_id: string }> => {
    if (!selectedPlanId) return [];
    return Array.from({ length: effectiveQuantity }, (_, i) => ({
      plan_id: advancedMode ? (numberPlans[i] || selectedPlanId) : selectedPlanId,
    }));
  }, [effectiveQuantity, advancedMode, numberPlans, selectedPlanId]);

  // ── Proration calculation (approximate — days remaining in billing period) ──
  const prorationInfo = useMemo(() => {
    if (!subInfo?.has_subscription || !subInfo.current_period_start || !subInfo.current_period_end) {
      return null;
    }

    const now = Math.floor(Date.now() / 1000);
    const periodStart = subInfo.current_period_start;
    const periodEnd = subInfo.current_period_end;
    const periodTotal = periodEnd - periodStart;
    const remaining = Math.max(0, periodEnd - now);
    const ratio = periodTotal > 0 ? remaining / periodTotal : 0;

    // Calculate monthly cost of new items
    let newMonthlyTotal = 0;
    for (const item of orderItems) {
      const plan = planCache.get(item.plan_id);
      newMonthlyTotal += Number(plan?.price?.value) || 0;
    }

    const proratedAmount = newMonthlyTotal * ratio;

    const nextBillingDate = new Date(periodEnd * 1000);
    const formattedDate = nextBillingDate.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    return { proratedAmount, formattedDate, nextBillingDate };
  }, [subInfo, orderItems, planCache]);

  // ── Summary calculation ──
  const summary = useMemo(() => {
    if (!selectedPlanId) return { items: [], totalMonthly: 0, isAnnual: false, withoutCommitment: false };

    // What one line actually buys. The list only ever holds one rhythm, so the selected plan
    // answers for the whole order.
    const interval = planCache.get(selectedPlanId)?.price?.interval ?? EBillingInterval.MONTH;

    const planCounts: { [key: string]: number } = {};
    for (let i = 0; i < effectiveQuantity; i++) {
      const planId = advancedMode ? (numberPlans[i] || selectedPlanId) : selectedPlanId;
      planCounts[planId] = (planCounts[planId] || 0) + 1;
    }

    let subtotalMonthly = 0;
    const items: { count: number; planName: string; planId: string; monthlyPrice: number }[] = [];

    Object.entries(planCounts).forEach(([planId, count]) => {
      const plan = planCache.get(planId);
      if (!plan) return;
      const unitPrice = plan.price?.value ?? 0;
      const linePrice = unitPrice * count;
      subtotalMonthly += linePrice;
      items.push({
        count,
        planName: plan.name ?? plan.id,
        planId,
        monthlyPrice: linePrice,
      });
    });

    // Numbers of a plan sold without commitment are created without one: only an order of those says so
    const withoutCommitment = Object.keys(planCounts).every((planId) => planCache.get(planId)?.no_commitment === true);

    return { items, totalMonthly: subtotalMonthly, isAnnual: interval === EBillingInterval.YEAR, suffix: billingIntervalAsSuffix(interval), withoutCommitment };
  }, [effectiveQuantity, advancedMode, selectedPlanId, numberPlans, planCache]);

  const handleOpenBillingPortal = async () => {
    if (!activeCompany || !authentication || !activeCompany.api_key) return;
    setBillingPortalLoading(true);
    try {
      const response = await createBillingPortalSession({
        company_id: activeCompany.id,
        company_api_key: activeCompany.api_key,
        return_url: window.location.href,
      }, authentication);
      window.open(response.url, '_blank');
    } catch (error) {
      console.error('Billing portal error:', error);
      message.error((error as Error).message || 'Erreur lors de l\'ouverture de la facturation');
    } finally {
      setBillingPortalLoading(false);
    }
  };

  const handleConfirm = () => {
    if (!selectedPlanId) return;

    const orderData: OrderData = {
      quantity: effectiveQuantity,
      paymentType,
      plan: selectedPlanId,
      advancedMode,
      items: orderItems,
      totalMonthly: summary.totalMonthly,
    };
    onConfirm(orderData);
  };

  const handleCancel = () => {
    setQuantity(1);
    setCustomQuantity('');
    setSelectedPlanId(plans.find((p) => p.id === 'lf-premium')?.id ?? plans[0]?.id ?? null);
    setAdvancedMode(false);
    setNumberPlans({});
    onCancel();
  };

  // ── Select options for advanced mode ──
  const planOptions = plans.map((plan) => ({
    value: plan.id,
    label: `${plan.name ?? plan.id} - ${plan.price?.formatted || '0€'}${billingIntervalAsSuffix(plan.price?.interval ?? EBillingInterval.MONTH)}`,
  }));

  return (
    <Modal
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
      centered
      width={620}
      className="order-number-modal"
      closable={true}
    >
      <div className="order-number-modal__content">
        <h2 className="order-number-modal__title">Commander des numéros</h2>

        {/* ── Service not started yet ── */}
        {isAwaitingOnboarding && (
          <Alert
            type="info"
            showIcon
            message="Votre service n'a pas encore démarré"
            description={
              onboardingDate
                ? `Vos numéros seront activés le ${onboardingDate.locale('fr').format('DD MMMM YYYY')}. Pour en ajouter d'ici là, contactez-nous : la facturation de tous vos numéros démarre ce jour-là.`
                : "Pour ajouter un numéro d'ici le démarrage, contactez-nous."
            }
            style={{ marginBottom: 16 }}
          />
        )}

        {/* ── No payment method warning ── */}
        {!hasValidPaymentLoading && !hasValidPayment && (
          <Alert
            type="warning"
            showIcon
            message="Moyen de paiement requis"
            description={
              <div>
                <p style={{ margin: '0 0 12px' }}>
                  Aucun moyen de paiement n&apos;est enregistré pour cette compagnie. Veuillez ajouter une carte bancaire avant de commander des numéros.
                </p>
                <Button
                  type="primary"
                  icon={<CreditCardOutlined />}
                  loading={billingPortalLoading}
                  onClick={handleOpenBillingPortal}
                >
                  Gérer les moyens de paiement
                </Button>
              </div>
            }
            style={{ marginBottom: 20 }}
          />
        )}

        {/* ── Quantity Section ── */}
        <div className="order-number-modal__section">
          <label className="order-number-modal__label">Combien de numéros ?</label>
          <div className="order-number-modal__quantity-row">
            <div className="order-number-modal__quantity-buttons">
              {QUANTITY_OPTIONS.map((q) => (
                <button
                  key={q}
                  className={`order-number-modal__quantity-btn ${quantity === q && !customQuantity ? 'order-number-modal__quantity-btn--active' : ''
                    }`}
                  onClick={() => handleQuantityClick(q)}
                >
                  {q}
                </button>
              ))}
            </div>
            <Input
              className="order-number-modal__quantity-input"
              placeholder=""
              value={customQuantity}
              onChange={(e) => handleCustomQuantityChange(e.target.value)}
            />
          </div>
        </div>

        {/* ── Plan Selection Section ── */}
        <div className="order-number-modal__section">
          <label className="order-number-modal__label">Plan</label>
          {plansLoading && plans.length === 0 ? (
            <div className="order-number-modal__plans-loading">
              <Spin />
              <span>Chargement des offres…</span>
            </div>
          ) : (
          <div className="order-number-modal__plans-row">
            {[...plans].sort((a, b) => (a.price?.value ?? 0) - (b.price?.value ?? 0)).map((plan) => (
              <button
                key={plan.id}
                className={`order-number-modal__plan-card ${selectedPlanId === plan.id ? 'order-number-modal__plan-card--active' : ''}`}
                onClick={() => setSelectedPlanId(plan.id)}
              >
                <span className="order-number-modal__plan-name">{plan.name ?? plan.id}</span>
                <div className="order-number-modal__plan-price-container">
                  <span className="order-number-modal__plan-price">
                    {plan.price?.formatted || '0€'}
                    <span className="order-number-modal__plan-price-suffix">{billingIntervalAsSuffix(plan.price?.interval ?? EBillingInterval.MONTH)} HT</span>
                  </span>
                </div>
                <div className="order-number-modal__plan-features">
                  <span className="order-number-modal__plan-feature">Forfait appels &amp; SMS partagé</span>
                  <span className="order-number-modal__plan-feature">
                    {formatPlanLeedbucks(plan, callRate, smsRate)}
                  </span>
                </div>
              </button>
            ))}
          </div>
          )}
        </div>

        {/* ── Advanced Mode ── */}
        <div className="order-number-modal__advanced-toggle">
          <Checkbox checked={advancedMode} onChange={(e) => setAdvancedMode(e.target.checked)}>
            Mode avancé : choisir un plan différent par numéro
          </Checkbox>
        </div>

        {/* ── Advanced Mode - Per Number Plan Selection ── */}
        {advancedMode && effectiveQuantity > 0 && (
          <div className="order-number-modal__advanced-section">
            <div className="order-number-modal__numbers-list">
              {Array.from({ length: effectiveQuantity }, (_, i) => (
                <div key={i} className="order-number-modal__number-row">
                  <span className="order-number-modal__number-label">Numéro {i + 1} :</span>
                  <Select
                    value={getPlanForSlot(i)}
                    onChange={(value) => handleNumberPlanChange(i, value)}
                    options={planOptions}
                    className="order-number-modal__number-select"
                    suffixIcon={<DownOutlined />}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Summary Section ── */}
        <div className="order-number-modal__summary">
          <div className="order-number-modal__summary-header">
            <span className="order-number-modal__summary-title">Récapitulatif</span>
          </div>
          <div className="order-number-modal__summary-items">
            {summary.items.map((item, index) => (
              <div key={index} className="order-number-modal__summary-item">
                <span>
                  {item.count}x {item.planName}
                </span>
                <span>{formatPrice(item.monthlyPrice)}€{summary.suffix} HT</span>
              </div>
            ))}
          </div>
          <div className="order-number-modal__summary-total">
            {subInfoLoading ? (
              <Spin size="small" />
            ) : prorationInfo ? (
              <>
                <div className="order-number-modal__summary-total-info">
                  <span className="order-number-modal__summary-total-label">
                    Ajusté au pro rata
                  </span>
                  <span className="order-number-modal__summary-total-sublabel">
                    facturé aujourd'hui puis de {formatPrice(summary.totalMonthly)}€{summary.suffix} dès le {prorationInfo.formattedDate}
                  </span>
                </div>
                <span className="order-number-modal__summary-total-price">
                  ~{formatPrice(prorationInfo.proratedAmount)}€{' '}
                  <span className="order-number-modal__summary-ht">HT</span>
                </span>
              </>
            ) : (
              <>
                <span>Total dû aujourd&apos;hui</span>
                <span className="order-number-modal__summary-total-price">
                  {formatPrice(summary.totalMonthly)}€{summary.suffix}{' '}
                  <span className="order-number-modal__summary-ht">HT</span>
                </span>
              </>
            )}
          </div>
          <p className="order-number-modal__summary-engagement">
            {prorationInfo
              ? 'Le montant exact sera calculé au moment de la commande au prorata de votre cycle de facturation en cours.'
              : summary.withoutCommitment
                ? summary.isAnnual
                  ? `Sans engagement - 1 prélèvement de ${formatPrice(summary.totalMonthly)}€ aujourd'hui`
                  : `Sans engagement - ${formatPrice(summary.totalMonthly)}€ par mois à partir d'aujourd'hui`
                : summary.isAnnual
                  ? `Engagement 12 mois - 1 prélèvement de ${formatPrice(summary.totalMonthly)}€ aujourd'hui`
                  : `Engagement 12 mois - 12 prélèvements de ${formatPrice(summary.totalMonthly)}€ à partir d'aujourd'hui`}
          </p>
          <p className="order-number-modal__summary-number-info">
            Les numéros seront attribués automatiquement.
          </p>
        </div>

        {/* ── Actions ── */}
        <div className="order-number-modal__actions">
          <Button className="order-number-modal__cancel-btn" onClick={handleCancel}>
            Annuler
          </Button>
          <Button
            type="primary"
            className="order-number-modal__confirm-btn"
            onClick={handleConfirm}
            loading={isLoading}
            disabled={effectiveQuantity < 1 || !selectedPlanId || isLoading || !hasValidPayment || isAwaitingOnboarding}
          >
            Commander
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default OrderNumberModal;
